/**
 * Single lander/offer row with optional preview weight overlay.
 */

import type { FC } from 'react';
import type { FlowLayer } from '@trafficpulse/shared';
import { VOLUUM_PATH_ITEM_WEIGHT_MAX } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';
import {
  usePreviewMode,
  usePreviewRow,
} from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import PreviewAwareWeightSlot from '@/features/flow-optimizer/components/preview/PreviewAwareWeightSlot';
import PreviewExcludedIndicator from '@/features/flow-optimizer/components/preview/PreviewExcludedIndicator';
import { FlowTagChips } from '@/features/flow-optimizer/components/preview/FlowTagChips';
import { StandardOfferCapChips } from '@/features/flow-optimizer/tabs/standard/v2/caps/StandardOfferCapChips';
import { excludedReasonText, showExcludedBadge } from '@/features/flow-optimizer/components/preview/preview-row.types';

import { getPathItemDisplay, type PathItemKind } from '../../../utils/path-item-display';
import PathItemRowActions from '../PathItemRowActions';
import { formatWeightSharePercent } from '../weight-share';

export interface PathItemRowProps {
  raw: unknown;
  kind: PathItemKind;
  noun: 'lander' | 'offer';
  pathId: string;
  totalWeight: number;
  itemWeightsLocked: boolean;
  onSetWeight: (weight: number) => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

function readEntityId(row: unknown, kind: PathItemKind): string {
  const { id } = getPathItemDisplay(row, kind);
  return id === '—' ? '' : id;
}

const PathItemRow: FC<PathItemRowProps> = ({
  raw,
  kind,
  noun,
  pathId,
  totalWeight,
  itemWeightsLocked,
  onSetWeight,
  onEdit,
  onDuplicate,
  onRemove,
}) => {
  const previewMode = usePreviewMode();
  const entityId = readEntityId(raw, kind);
  const previewRow = usePreviewRow(kind as FlowLayer, entityId, pathId);
  const itemActive = (raw as { active?: boolean }).active !== false;
  const previewLocked =
    previewMode && previewRow != null && !showExcludedBadge(previewRow, itemActive);
  const { title, subtitle } = getPathItemDisplay(raw, kind);
  const tip = subtitle ? `${title}\n${subtitle}` : title;
  const w = (raw as { weight?: number }).weight ?? 0;
  const share = formatWeightSharePercent(w, totalWeight);
  const gridCols = previewLocked
    ? 'grid-cols-[1.5rem_minmax(0,1fr)_minmax(12rem,auto)_auto]'
    : 'grid-cols-[1.5rem_minmax(0,1fr)_5rem_3.5rem_auto]';

  return (
    <li className="transition-colors group">
      <div className="w-full px-1.5 py-1">
        <div className={cn('grid w-full items-center gap-x-1.5 gap-y-0', gridCols)}>
          <div className="h-6 w-6 shrink-0" aria-hidden />
          <div className="min-w-0">
            {/* The name keeps ≥5rem and takes the rest of the line; the chips stay
                whole and drop to a second line only when the row is too narrow. */}
            <div className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5" title={tip}>
              <span className="min-w-0 flex-[1_1_5rem] truncate text-xs font-medium leading-tight text-foreground">
                {title}
              </span>
              <FlowTagChips
                layer={kind as FlowLayer}
                itemId={entityId}
                pathId={pathId}
                className="shrink-0 gap-1"
              />
              {kind === 'offers' ? <StandardOfferCapChips offerIds={[entityId]} /> : null}
              {showExcludedBadge(previewRow, itemActive) ? (
                <PreviewExcludedIndicator
                  title={excludedReasonText(previewRow)}
                  row={previewRow}
                  name={title}
                />
              ) : null}
            </div>
            {subtitle ? (
              <span className="mt-0.5 block truncate font-mono text-[10px] leading-tight text-muted-foreground">
                {subtitle}
              </span>
            ) : null}
          </div>
          {!previewLocked ? (
            <div className="flex items-center justify-end text-right text-[11px] leading-none tabular-nums text-muted-foreground">
              ({share})
            </div>
          ) : null}
          <div className="flex items-center justify-end">
            <PreviewAwareWeightSlot
              name={title}
              previewRow={previewRow}
              previewMode={previewMode}
              active={itemActive}
              value={w}
              onChange={onSetWeight}
              ariaLabel={`Weight for ${noun} ${title}`}
              integerMax={VOLUUM_PATH_ITEM_WEIGHT_MAX}
              forceReadOnly={!previewMode && itemWeightsLocked}
              forceReadOnlyHint="Weight is managed by Voluum (optimization on)"
            />
          </div>
          <div className="flex items-center justify-end">
            <PathItemRowActions
              entityLabel={noun}
              onEdit={onEdit}
              onDuplicate={onDuplicate}
              onRemove={onRemove}
            />
          </div>
        </div>
      </div>
    </li>
  );
};

export default PathItemRow;
